<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-6">
			<div class="flex items-center gap-4">
				<button
					type="button"
					class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50"
					@click="goBack"
				>
					<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
					</svg>
				</button>
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">
						{{ flexGroup?.name || 'Loading...' }}
					</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0">Flex Group ID: {{ flexGroupId }}</p>
				</div>
			</div>
		</header>

		<!-- Loading State -->
		<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
			<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span class="ml-3 text-gray-500 text-sm">Loading flex group...</span>
		</div>

		<!-- Main Content -->
		<main v-else class="p-6">
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<div class="p-6">
					<h3 class="text-base font-semibold text-gray-900 m-0 mb-4">Flex Group Properties</h3>
					<div class="max-w-lg">
						<label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
						<div class="flex gap-2">
							<input
								v-model="flexGroup.name"
								type="text"
								placeholder="Enter flex group name"
								class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
							>
							<button
								type="button"
								:disabled="isSaving"
								class="py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
								@click="saveFlexGroup"
							>
								{{ isSaving ? 'Saving...' : 'Save' }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { FlexGroupModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import FlexGroupService from 'services/FlexGroupService';

export default defineComponent({
	name: 'EditFlexGroupPage',
	setup() {
		const route = useRoute();
		const router = useRouter();

		const flexGroupId = route.params.id as string;

		const flexGroup = ref<Partial<FlexGroupModel>>({});
		const isLoading = ref(true);
		const isSaving = ref(false);

		const goBack = () => {
			router.push('/flexgroup');
		};

		const loadFlexGroup = async () => {
			isLoading.value = true;
			try {
				const data = await FlexGroupService.get(flexGroupId);
				flexGroup.value = data;
			} catch (error: any) {
				console.error('Failed to load flex group:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load flex group',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const saveFlexGroup = async () => {
			isSaving.value = true;
			try {
				await FlexGroupService.update(flexGroupId, flexGroup.value);
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Flex group saved successfully',
					showConfirmButton: false,
					timer: 3000,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save flex group',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		onMounted(() => {
			loadFlexGroup();
		});

		return {
			flexGroupId,
			flexGroup,
			isLoading,
			isSaving,
			goBack,
			loadFlexGroup,
			saveFlexGroup,
		};
	},
});
</script>
